import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useScheduledNotificationsQuery, useSendScheduledNotificationMutation, useDeleteScheduledNotificationsMutation } from '../../hooks/useScheduledNotificationsQuery';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { NotificationStatus, ScheduledNotificationResponse, getNotificationStatus, DeleteScheduledNotificationsRequest, NotificationsTabsType } from '../../types/api/notifications';
import { formatDate, formatTime, formatToLocalTime } from '../../helpers/dates';
import Segment from '../navigation/Segment/Segment';
import { notificationsTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { useAlert } from '../../context/alert';
import TableActionsBanner from '../common/TableActionsBanner';
import { notificationsTableConfig } from './NotificationsList+Table';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { locale } from '../../helpers/strings';
import { CheckNotificationStatus } from './NotificationsList+CheckStatus';
import { NotificationModal } from './components/NotificationModal';
import { LanguageResponse } from '../../types/api/lang';
import { Translation } from '../../types/api/common';
import { fetchLanguages } from '../../services/lang';
import { renderTruncatedText } from '../../helpers/strings';
import { pageSize } from '../../services/settings';
import { Stack, Checkbox, Button } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { PlusIcon, XCircleIcon, PencilIcon, PaperAirplaneIcon, TrashIcon, HourglassIcon, CalendarIcon, ClockIcon, CheckIcon } from '@primer/octicons-react'

const EMPTY_SET = new Set<string>();

export default function NotificationsList() {
    type NotificationAction = 'send' | 'delete';

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const rawtabsType = searchParams.get('type');
    const tabsType = useMemo(() => rawtabsType || undefined, [rawtabsType]);

    const [pageIndex, setPageIndex] = React.useState(1)
    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [languagesLoaded, setLanguagesLoaded] = useState(false);

    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const [selectedNotification, setSelectedNotification] = useState<ScheduledNotificationResponse | undefined>(undefined);
    const [openNotificationModal, setOpenNotificationModal] = React.useState(false)

    useEffect(() => {
        const type = tabsType as NotificationsTabsType

        setPageIndex(1);
        setSelectedRows(EMPTY_SET);

        switch (type) {
            case 'all':
            case undefined:
                refetch()
                break
            case 'scheduled':
                break
            case 'sended':
                break
        }
    }, [tabsType]);

    const queryClient = useQueryClient();

    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const { mutate: sendNotification, isPending: isSendingProcess } = useSendScheduledNotificationMutation();
    const { mutate: deleteNotifications, isPending: isDeletingProcess } = useDeleteScheduledNotificationsMutation();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
            setLanguagesLoaded(true);
        },
        onError: (error: any) => {
            setAllLangs([]);
            setLanguagesLoaded(true);
        }
    });

    const getPlannedDateFrom = () => {
        if (tabsType === 'scheduled') {
            return "1970-01-01T00:00:00"
        }
        return undefined
    }

    const isSendedTab = () => {
        if (tabsType === 'scheduled') {
            return false
        }
        return tabsType === 'sended' ? true : undefined
    }

    const {
        isLoading: isLoadingNotifications,
        data,
        refetch,
        isFetched,
        isError
    } = useScheduledNotificationsQuery(
        undefined, //is_canceled
        isSendedTab(), //is_sent
        getPlannedDateFrom(), // planned_dt_from
        undefined, // planned_dt_to
        undefined, // search
        pageIndex,
        pageSize,
        {
            enabled: languagesLoaded
        }
    );

    // Загружаем языки при монтировании компонента
    useEffect(() => {
        fetchLanguagesRequest({});
    }, []);

    const isLoading = isLoadingLang || isLoadingNotifications;

    useCleanPaginationURL();
    usePaginationTextReplace()

    // Color rows


    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);

    const emptyListDescription = `В сервисе не создано
    ни одного уведомления`;

    // ACTIONS
    const useNotificationAction = (action: NotificationAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'send':
                        handleSend(ids);
                        break;
                    case 'delete':
                        handleDelete([{ uuids: ids }]);
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const sendSelectedNotifications = useNotificationAction('send', actionsAlertId);
    const deleteSelectedNotifications = useNotificationAction('delete', actionsAlertId);

    const handleActionResult = (
        count: number,
        singularMessage: string,
        pluralMessage: string
    ) => {
        // Update UI
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        refetch();
        // Alert
        if (count === 1) {
            setInfo(singularMessage);
        } else {
            setInfo(pluralMessage);
        }
    };

    const handleSend = (ids: string[]) => {
        if (ids.length === 0) return;

        let successCount = 0;

        ids.forEach(id => {
            sendNotification(id, {
                onSuccess: (data) => {
                    successCount++;
                    if (successCount === ids.length) {
                        dismissTableActionAlert()
                        setSuccess('Уведомления отправлены');
                        refetch();
                    }
                }
            });
        });
    };

    // Удалить уведомления
    const handleDelete = (requests: DeleteScheduledNotificationsRequest[]) => {
        if (requests.length === 0) return;

        let successCount = 0;
        let totalDeleted = 0;

        requests.forEach(request => {
            deleteNotifications(request, {
                onSuccess: (data) => {
                    successCount++;
                    totalDeleted += data.count;

                    if (successCount === requests.length) {
                        dismissTableActionAlert()
                        handleActionResult(totalDeleted, 'Уведомление удалено', totalDeleted + ' уведомления удалены');
                        refetch();
                    }
                }
            });
        });
    };

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined

        const isSended = selectedIsSended(true)
        if (isSended) {
            props = createAlertPropsForSended(count)
        } else {
            props = createAlertPropsForScheduled(count)
        }

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    useEffect(() => {
    }, [actionsAlertId])

    // TABLE ACTIONS ALERT
    function createAlertPropsForScheduled(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);


        const editFirstSelectedNotification = () => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const id = Array.from(selectedItems)[0];
                const findedNotifications = data?.list.filter(el => el.id === id) ?? []
                if (findedNotifications.length === 1) {
                    editSingleNotification(findedNotifications[0])
                    dismissTableActionAlert()
                }
            }
        }

        const buttons = [
            <Button key="clear-button"
                variant="default"
                disabled={isSendingProcess || isDeletingProcess}
                leadingVisual={PencilIcon}
                onClick={editFirstSelectedNotification}>
                Редактировать
            </Button>,
            <Button key="clear-button"
                variant="default"
                disabled={isSendingProcess || isDeletingProcess}
                leadingVisual={PaperAirplaneIcon}
                onClick={sendSelectedNotifications}>
                Отправить сейчас
            </Button>,
            <Button key="clear-button"
                variant="danger"
                disabled={isSendingProcess || isDeletingProcess}
                leadingVisual={TrashIcon}
                onClick={deleteSelectedNotifications}>
                Удалить
            </Button>
        ]

        if (count > 1) {
            buttons.shift();
        }
        if (selectedIsSended(true) && buttons.length > 1) {
            buttons.shift();
        }

        return {
            count: count,
            title: title,
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createAlertPropsForSended(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);

        const editFirstSelectedNotification = () => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const id = Array.from(selectedItems)[0];
                const findedNotifications = data?.list.filter(el => el.id === id) ?? []
                if (findedNotifications.length === 1) {
                    editSingleNotification(findedNotifications[0])
                    dismissTableActionAlert()
                }
            }
        }

        const buttons = [
            <Button key="clear-button"
                variant="danger"
                disabled={isSendingProcess || isDeletingProcess}
                leadingVisual={TrashIcon}
                onClick={deleteSelectedNotifications}>
                Удалить
            </Button>
        ]

        return {
            count: count,
            title: title,
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {

        let checker = new CheckNotificationStatus(data?.list || [])
        let isAllNotificationsStatusEqual = checker.isAllNotificationsStatusEqual()

        if (data) {
            if (isSelected) {
                if (isAllNotificationsStatusEqual) {
                    setSelectedRows(new Set(data.list.map(row => row.id)));
                } else {
                    setError('Выделение невозможно')
                }
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const totalRows = data.list.length;
            const selectedFromCurrentData = data.list.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        let selectedSended = selectedIsSended(true)
        let checker = new CheckNotificationStatus(data?.list || [])
        let currentRowBlocked = checker.isEqualStatus(rowId, NotificationStatus.SENT)

        if (selectedRowsRef.current.size === 0) {
            rowSelect(rowId, isSelected)
        } else {
            if (selectedSended) {
                if (currentRowBlocked) {
                    rowSelect(rowId, isSelected)
                } else {
                    setError('Выделение невозможно')
                }
            } else {
                if (currentRowBlocked) {
                    setError('Выделение невозможно')
                } else {
                    rowSelect(rowId, isSelected)
                }
            }
        }
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    // CHECK STATUS
    const selectedIsSended = (isSended: boolean): boolean => {
        let selected = selectedRowsRef.current
        const ids = Array.from(selected);
        let checker = new CheckNotificationStatus(data?.list || [])
        return checker.allEqualStatus(ids, isSended ? NotificationStatus.SENT : NotificationStatus.SCHEDULED);
    };

    const createNewCategory = () => {
        setOpenNotificationModal(true)
    }

    const categoryDismissed = () => {
        setSelectedNotification(undefined)
        setOpenNotificationModal(false)
    }

    const categorySaved = () => {
        setSelectedNotification(undefined)
        setOpenNotificationModal(false)
        refetch()
    }

    const sendSingleNotification = (notification: ScheduledNotificationResponse) => {
        sendNotification(notification.id, {
            onSuccess: (data) => {
                dismissTableActionAlert()
                setSuccess('Уведомление отправлено');
                refetch();
            }
        });
    }

    const editSingleNotification = (notification: ScheduledNotificationResponse) => {
        setSelectedNotification(notification)
        setOpenNotificationModal(true)
    }

    const getRussianOrFirstFieldValue = (notification: ScheduledNotificationResponse, fieldName: string): string => {
        const translations = notification.translations
        const russianTranslation = translations.find(
            (t) => t.languageIsoCode === "ru"
        );
        const russianField = russianTranslation?.fields.find(
            (f) => f.fieldName === fieldName
        );

        if (russianField?.value?.trim()) {
            return russianField.value;
        }

        const firstTranslation = translations[0];
        const firstField = firstTranslation?.fields.find(
            (f) => f.fieldName === fieldName
        );

        return firstField?.value || "";
    };

    const getLangsByIsoCodes = (translations: Translation[]) => {
        return translations.map(translation => {
            const lang = allLangs.find(lang => lang.isoCode === translation.languageIsoCode);
            return lang?.titleRu || lang?.titleOrigin;
        }).filter(Boolean);
    };

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Segment items={notificationsTabsData} />
                    <Button
                        variant="default"
                        size="small"
                        onClick={createNewCategory}
                        leadingVisual={PlusIcon}
                    >
                        Создать уведомление
                    </Button>
                </Stack>
            }
            content={
                <Stack>
                    {openNotificationModal && (<NotificationModal
                        isOpen={openNotificationModal}
                        notification={selectedNotification}
                        onDidSaved={categoryDismissed}
                        onClose={categorySaved}
                    />)}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="notifications-loading"
                            rows={pageSize}
                            columns={notificationsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Уведомления не найдены'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="notifications-table"
                                data={data.list}
                                columns={[
                                    ...(tabsType === 'all' || tabsType === undefined || tabsType === 'scheduled' ? [{
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (notification: ScheduledNotificationResponse) => {
                                            switch (getNotificationStatus(notification)) {
                                                case NotificationStatus.SCHEDULED:
                                                    return (
                                                        <Checkbox
                                                            checked={selectedRows.has(notification.id)}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                handleRowSelect(notification.id, e.target.checked)
                                                            }
                                                            aria-label={`Выделить ${notification.id}`}
                                                        />
                                                    )
                                                default:
                                                    return null;
                                            }
                                        }
                                    }] : []),

                                    {
                                        id: 'title',
                                        header: 'Заголовок',
                                        renderCell: (row) => {
                                            const notification = row as ScheduledNotificationResponse;
                                            return getRussianOrFirstFieldValue(notification, 'title')
                                        }
                                    },
                                    {
                                        id: 'body',
                                        header: 'Тело',
                                        renderCell: (row) => {
                                            const notification = row as ScheduledNotificationResponse;
                                            return getRussianOrFirstFieldValue(notification, 'body')
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: 'Статус и время',
                                        renderCell: (row) => {
                                            const notification = row as ScheduledNotificationResponse;
                                            const getIcon = (notification: ScheduledNotificationResponse) => {
                                                switch (getNotificationStatus(notification)) {
                                                    case NotificationStatus.SCHEDULED:
                                                        return <HourglassIcon />;
                                                    case NotificationStatus.CANCELED:
                                                        return <XCircleIcon />;
                                                    case NotificationStatus.SENT:
                                                    case NotificationStatus.EXPIRED:
                                                        return <CheckIcon />;
                                                    default:
                                                        return null;
                                                }
                                            };
                                            const getStatusTitle = (notification: ScheduledNotificationResponse) => {
                                                switch (getNotificationStatus(notification)) {
                                                    case NotificationStatus.SCHEDULED:
                                                        return "Запланировано"
                                                    case NotificationStatus.CANCELED:
                                                        return "Отменено"
                                                    case NotificationStatus.SENT:
                                                    case NotificationStatus.EXPIRED:
                                                        return "Отправлено"
                                                    default:
                                                        return "";
                                                }
                                            };
                                            const getNotificationDate = (notification: ScheduledNotificationResponse) => {
                                                switch (getNotificationStatus(notification)) {
                                                    case NotificationStatus.SCHEDULED:
                                                        return notification.plannedDt
                                                    case NotificationStatus.CANCELED:
                                                        return notification.canceledAt || notification.plannedDt
                                                    case NotificationStatus.SENT:
                                                    case NotificationStatus.EXPIRED:
                                                        return notification.plannedDt
                                                    default:
                                                        return notification.plannedDt
                                                }
                                            };

                                            const formatTimeTitle = (notification: ScheduledNotificationResponse) => {
                                                //console.log('formatTimeTitle SENT', notification, notification.translations, notification.translations, formatTime(notification.plannedDt), formatToLocalTime(notification.plannedDt))
                                                const date = getNotificationDate(notification)

                                                switch (getNotificationStatus(notification)) {
                                                    case NotificationStatus.SCHEDULED:
                                                        return formatTime(date)
                                                    case NotificationStatus.CANCELED:
                                                        return formatTime(date)
                                                    case NotificationStatus.SENT:
                                                    case NotificationStatus.EXPIRED:
                                                        return formatToLocalTime(date)
                                                    default:
                                                        return formatTime(date)
                                                }
                                            };

                                            const statusView = (
                                                <Stack direction="horizontal" gap="condensed" align="center">
                                                    {getIcon(notification)}
                                                    {getStatusTitle(notification)}
                                                </Stack>
                                            );

                                            return (
                                                <Stack direction="horizontal" gap="condensed" align="center">
                                                    {statusView}
                                                    <Stack direction="horizontal" gap="condensed" align="center">
                                                        <CalendarIcon />
                                                        {formatDate(getNotificationDate(notification))}
                                                    </Stack>
                                                    <Stack direction="horizontal" gap="condensed" align="center">
                                                        <ClockIcon />
                                                        {formatTimeTitle(notification)}
                                                    </Stack>
                                                </Stack>
                                            )
                                        }
                                    },
                                    {
                                        id: 'langs',
                                        header: 'Языки',
                                        renderCell: (row) => {
                                            const notification = row as ScheduledNotificationResponse;
                                            const titleLangs = getLangsByIsoCodes(notification.translations)
                                            const langs = titleLangs
                                                .map((title, index) => index === 0 ? title : title?.toLowerCase())
                                                .join(', ');
                                            return renderTruncatedText(langs, 32)
                                        }
                                    },
                                    {
                                        id: 'actions',
                                        header: '',
                                        renderCell: (row) => {
                                            const notification = row as ScheduledNotificationResponse;
                                            switch (getNotificationStatus(notification)) {
                                                case NotificationStatus.SCHEDULED:
                                                    return (
                                                        <Stack direction="horizontal" gap="condensed" >
                                                            <Button
                                                                onClick={() => editSingleNotification(notification)}
                                                                disabled={selectedRows.size !== 0 || isSendingProcess || isDeletingProcess}
                                                                size="small"
                                                                className={selectedRows.size !== 0 ? 'opacity-50' : ''}>
                                                                Редактировать</Button>
                                                            <Button
                                                                onClick={() => sendSingleNotification(notification)}
                                                                disabled={selectedRows.size !== 0 || isSendingProcess || isDeletingProcess}
                                                                size="small"
                                                                className={selectedRows.size !== 0 ? 'opacity-50' : ''}>
                                                                Отправить</Button>
                                                        </Stack>
                                                    )
                                                default:
                                                    return (<></>)
                                            }
                                        }
                                    }
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}